import React, { useEffect, useRef, useState } from 'react';
import { useCompact } from './_layout';
import { playWord, playOops } from './_audio';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5', sky: '#BAE6FD',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';

const ASSETS = '/games/vocab_train_spell';

const ANIM_CSS = `
@keyframes tcRoll { 0%{transform:translateX(-50%)} 100%{transform:translateX(calc(-50% + 70vw))} }
@keyframes tcCarShake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
@keyframes tcSmoke { 0%{transform:translate(0,0) scale(0.6); opacity:.85} 100%{transform:translate(-30px,-50px) scale(1.4); opacity:0} }
@keyframes tcRibbon { 0%{transform:translate(0,0) rotate(0); opacity:1} 100%{transform:translate(var(--dx),var(--dy)) rotate(var(--dr)); opacity:0} }
@keyframes tcBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
@keyframes tcFly {
  0%   { transform: translate(-50%, -50%) scale(1)    rotate(0); }
  55%  { transform: translate(calc(-50% + var(--toX) * 0.55), calc(-50% + var(--toY) * 0.55 - 28px)) scale(1.15) rotate(-160deg); }
  100% { transform: translate(calc(-50% + var(--toX)), calc(-50% + var(--toY))) scale(0.85) rotate(-360deg); }
}
.tc-car-dragging { z-index: 1000; pointer-events: none; transform: scale(1.1); box-shadow: 0 10px 24px rgba(0,0,0,0.35) !important; }
`;

// Quick "pluck" SFX when a car is picked.
function playCarPluck() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(720, now);
  osc.frequency.exponentialRampToValueAtTime(420, now + 0.18);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.22, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.22);
  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 350);
}

// "Tu tu" whistle — 2 sawtooth toots through lowpass. Total duration ~780ms.
function playWhistle() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  const playToot = (startT, freq, dur, vol = 0.18) => {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(freq * 0.92, startT);
    osc.frequency.linearRampToValueAtTime(freq, startT + 0.05);
    osc.frequency.linearRampToValueAtTime(freq * 0.95, startT + dur);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startT);
    gain.gain.linearRampToValueAtTime(vol, startT + 0.04);
    gain.gain.linearRampToValueAtTime(vol * 0.85, startT + dur - 0.05);
    gain.gain.linearRampToValueAtTime(0, startT + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1500;
    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start(startT);
    osc.stop(startT + dur + 0.05);
  };

  playToot(now, 520, 0.28);
  playToot(now + 0.38, 520, 0.4);

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
}

// Chuff-chuff puffs — plays while train rolls.
function playChuffs() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  for (let i = 0; i < 8; i++) {
    const t = now + i * 0.22;
    const bufSize = Math.floor(ctx.sampleRate * 0.14);
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let j = 0; j < bufSize; j++) data[j] = (Math.random() * 2 - 1) * 0.5;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 380;
    filter.Q.value = 6;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.22, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    noise.connect(filter).connect(gain).connect(ctx.destination);
    noise.start(t);
    noise.stop(t + 0.14);
  }

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 2500);
}

function injectCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('train-cloze-css')) return;
  const el = document.createElement('style');
  el.id = 'train-cloze-css';
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

export default function TrainClozeGame({ payload, onAnswer, disabled }) {
  const compact = useCompact();
  const cars = payload.cars || [];
  const stemEn = payload.stem_en || '';
  const stemVi = payload.stem_vi || '';
  const correctCarId = payload.correct_car_id;

  const [attachedCar, setAttachedCar] = useState(null);
  const [drag, setDrag] = useState(null);
  const [flying, setFlying] = useState(null);
  const [grading, setGrading] = useState(null); // 'correct' | 'wrong' | 'gaveup'
  const [chugging, setChugging] = useState(false);
  const [wordZoom, setWordZoom] = useState(false);
  const [retries, setRetries] = useState(0);

  const slotRef = useRef(null);
  const carRefs = useRef({});
  const startPosRef = useRef(null);
  const wasDraggedRef = useRef(false);
  const evaluatingRef = useRef(false);

  useEffect(() => { injectCss(); }, []);

  useEffect(() => {
    if (chugging) playChuffs();
  }, [chugging]);

  // ── Drag / click handlers ──────────────────────────────────────────
  const getPoint = (e) => ('touches' in e ? e.touches[0] : e);
  const CLICK_THRESHOLD_PX = 6;

  const evaluateAttach = (car) => {
    setAttachedCar(car);
    const isCorrect = car.id === correctCarId;
    if (isCorrect) {
      setGrading('correct');
      // Sequence: whistle → zoom card + replay audio → chug off
      playWhistle();
      setTimeout(() => {
        setWordZoom(true);
        playWord(payload.audio_url || car.audio_url, car.word_en || payload.stem_en);
      }, 900);
      setTimeout(() => {
        setChugging(true);
      }, 2400);
      setTimeout(() => {
        if (onAnswer) onAnswer({
          is_correct: true,
          user_answer: car.word_en || car.tag_code,
        });
      }, 6000);
    } else {
      setGrading('wrong');
      playOops();
      setRetries(r => r + 1);
      // Auto-eject after the shake animation
      setTimeout(() => {
        setAttachedCar(null);
        setGrading(null);
        evaluatingRef.current = false;
      }, 1100);
    }
  };

  const triggerCarFly = (car) => {
    if (evaluatingRef.current || grading === 'correct' || grading === 'revealed') return;
    const cardEl = carRefs.current[car.id];
    const slotEl = slotRef.current;
    if (!cardEl || !slotEl) {
      evaluatingRef.current = true;
      evaluateAttach(car);
      return;
    }
    const cardRect = cardEl.getBoundingClientRect();
    const slotRect = slotEl.getBoundingClientRect();
    const startX = cardRect.left + cardRect.width / 2;
    const startY = cardRect.top + cardRect.height / 2;
    const endX = slotRect.left + slotRect.width / 2;
    const endY = slotRect.top + slotRect.height / 2;
    evaluatingRef.current = true;
    playCarPluck();
    setFlying({
      id: car.id, image_url: car.image_url, word_en: car.word_en,
      startX, startY, toX: endX - startX, toY: endY - startY,
    });
    setTimeout(() => {
      setFlying(null);
      evaluateAttach(car);
    }, 450);
  };

  const startDrag = (e, car) => {
    if (disabled || grading === 'correct' || grading === 'revealed' || evaluatingRef.current) return;
    const point = getPoint(e);
    startPosRef.current = { x: point.clientX, y: point.clientY };
    wasDraggedRef.current = false;
    setDrag({ id: car.id, x: point.clientX, y: point.clientY, car });
    e.preventDefault();
  };

  const endDrag = (e) => {
    if (!drag) return;

    // CLICK → fly the car to the slot
    if (!wasDraggedRef.current) {
      triggerCarFly(drag.car);
      setDrag(null);
      startPosRef.current = null;
      return;
    }

    // DRAG → if released on slot, attach instantly; otherwise fly
    const point = getPoint(e);
    const x = point.clientX, y = point.clientY;
    let onSlot = false;
    if (slotRef.current) {
      const r = slotRef.current.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        onSlot = true;
      }
    }
    if (onSlot) {
      evaluatingRef.current = true;
      evaluateAttach(drag.car);
    } else {
      triggerCarFly(drag.car);
    }
    setDrag(null);
    startPosRef.current = null;
  };

  useEffect(() => {
    if (!drag) return;
    const move = (e) => {
      const p = ('touches' in e ? e.touches[0] : e);
      if (startPosRef.current && !wasDraggedRef.current) {
        const dx = p.clientX - startPosRef.current.x;
        const dy = p.clientY - startPosRef.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > CLICK_THRESHOLD_PX) {
          wasDraggedRef.current = true;
        }
      }
      setDrag(d => d ? { ...d, x: p.clientX, y: p.clientY } : null);
      if (wasDraggedRef.current) e.preventDefault();
    };
    const up = (e) => endDrag(e);
    window.addEventListener('mousemove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
    // eslint-disable-next-line
  }, [drag]);

  const replay = () => playWord(payload.audio_url, payload.stem_en);

  // Child chose to see the answer: attach the correct car, show the full
  // sentence + word meaning, but the question does NOT count (is_correct: false).
  const revealAnswer = () => {
    if (grading === 'correct' || grading === 'revealed') return;
    const correct = cars.find(c => c.id === correctCarId);
    evaluatingRef.current = true;
    setGrading('revealed');
    setAttachedCar(correct || null);
    setWordZoom(true);
    playWord(payload.audio_url || correct?.audio_url, payload.answer_word_en || payload.stem_en);
    setTimeout(() => {
      if (onAnswer) onAnswer({ is_correct: false, user_answer: 'revealed' });
    }, 5000);
  };

  const renderStem = (stem, blankSlot) => {
    const parts = stem.split(/___+/);
    return parts.map((p, i) => (
      <React.Fragment key={i}>
        {p}
        {i < parts.length - 1 ? blankSlot : null}
      </React.Fragment>
    ));
  };

  return (
    <div style={{
      width: 'calc(100vw - 32px)',
      maxWidth: 1600,
      marginLeft: 'calc(-50vw + 50% + 16px)',
      marginRight: 'calc(-50vw + 50% + 16px)',
      fontFamily: FONT_BODY,
      padding: 0,
      height: 'calc(100vh - 120px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div
        style={{
          position: 'relative',
          width: compact ? '100%' : 'min(100%, calc((100vh - 120px) * 1672 / 941))',
          aspectRatio: compact ? '3 / 4' : '1672 / 941',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(139,111,71,0.18)',
          userSelect: 'none',
        }}
      >
        {/* Background */}
        <img
          src={`${ASSETS}/background.png`}
          alt=""
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />

        {/* Sentence card (top center) — zooms in after whistle to celebrate */}
        <div style={{
          position: 'absolute', top: '4%', left: '50%',
          transform: wordZoom
            ? 'translateX(-50%) scale(1.15)'
            : 'translateX(-50%) scale(1)',
          transformOrigin: 'top center',
          transition: 'transform 0.55s cubic-bezier(.34, 1.56, .64, 1)',
          width: compact ? '92%' : 'clamp(360px, 65%, 1000px)',
          zIndex: wordZoom ? 5 : 'auto',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            border: `4px solid ${HT.brownDark}`,
            borderRadius: 20,
            padding: 'clamp(10px, 1.5vw, 22px) clamp(14px, 2vw, 30px)',
            boxShadow: `0 5px 0 ${HT.brownDark}`,
          }}>
            <div style={{
              fontSize: compact ? 16 : 'clamp(14px, 1.9vw, 26px)',
              fontWeight: 800,
              fontFamily: FONT_DISPLAY,
              color: HT.text,
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1.4, gap: 4,
            }}>
              {renderStem(stemEn, (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  minWidth: 'clamp(70px, 9vw, 140px)',
                  height: 'clamp(32px, 3.8vw, 50px)',
                  background: attachedCar
                    ? (grading === 'correct' ? HT.greenSoft : HT.pinkBg)
                    : HT.yellowBg,
                  border: `3px dashed ${grading === 'wrong' ? HT.coral : HT.brownDark}`,
                  borderRadius: 10,
                  margin: '0 6px',
                  padding: '0 10px',
                  fontSize: compact ? 16 : 'clamp(14px, 2vw, 24px)',
                  fontWeight: 800,
                  color: attachedCar
                    ? (grading === 'correct' ? HT.greenDark : HT.coralDark)
                    : HT.textMuted,
                  transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                }}>
                  {attachedCar ? attachedCar.word_en : '?'}
                </span>
              ))}
            </div>
            {stemVi && (
              <div style={{
                marginTop: 8,
                fontSize: compact ? 12 : 'clamp(11px, 1.3vw, 17px)',
                color: HT.textSoft,
                fontStyle: 'italic',
                textAlign: 'center',
                fontWeight: 600,
              }}>💭 {stemVi}</div>
            )}
            {wordZoom && (payload.name_vi || payload.definition_vi) && (
              <div style={{
                marginTop: 10, paddingTop: 8,
                borderTop: `2px dashed ${HT.border}`,
                textAlign: 'center',
              }}>
                <div style={{
                  fontSize: compact ? 14 : 'clamp(13px, 1.6vw, 20px)', fontWeight: 800,
                  fontFamily: FONT_DISPLAY, color: HT.greenDark,
                }}>
                  ✨ {payload.answer_word_en}{payload.name_vi ? ` = ${payload.name_vi}` : ''}
                </div>
                {payload.definition_vi && (
                  <div style={{
                    marginTop: 4, fontSize: 'clamp(10px, 1.15vw, 14px)',
                    color: HT.textSoft, fontWeight: 600, lineHeight: 1.35,
                  }}>
                    📖 {payload.definition_vi}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sound button — top-left of the stage */}
        <button
          onClick={replay}
          style={{
            position: 'absolute',
            top: '3%', left: '3%',
            width: 'clamp(36px, 4.2vw, 52px)',
            aspectRatio: '1 / 1',
            borderRadius: '50%', border: `2px solid ${HT.white}`,
            background: HT.coralDark,
            color: HT.white,
            cursor: 'pointer',
            boxShadow: `0 3px 0 ${HT.coralPress}`,
            fontSize: 'clamp(14px, 1.6vw, 20px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
            zIndex: 6,
          }}
          title="Nghe lại"
        >🔊</button>

        {/* Train — single empty car (drop zone) + engine on the right.
            Engine sprite mirrored to face RIGHT. Chugs LEFT → RIGHT over 2.6s. */}
        <div style={{
          position: 'absolute',
          bottom: '34%', left: '50%',
          transform: 'translateX(-50%)',
          width: compact ? '94%' : 'auto',
          justifyContent: 'center',
          display: 'flex', alignItems: 'flex-end', gap: '0.4%',
          animation: chugging ? 'tcRoll 2.6s ease-in forwards' : 'none',
          zIndex: 3,
        }}>
          {/* Empty / attached car — the drop zone */}
          <div
            ref={slotRef}
            style={{
              position: 'relative',
              width: compact ? '30%' : 'clamp(110px, 16vw, 200px)',
              flexShrink: 0,
              animation: grading === 'wrong' ? 'tcCarShake 0.35s ease-in-out 2' : 'none',
            }}
          >
            <img
              src={`${ASSETS}/train_body_no_question.png`}
              alt=""
              style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
            />
            <div style={{
              position: 'absolute',
              top: '18%', left: '10%', right: '10%', height: '48%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              {attachedCar ? (
                attachedCar.image_url ? (
                  <img
                    src={attachedCar.image_url}
                    alt={attachedCar.word_en}
                    style={{
                      maxWidth: '100%', maxHeight: '100%',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span style={{
                    fontSize: compact ? 15 : 'clamp(14px, 1.8vw, 22px)',
                    fontWeight: 800, fontFamily: FONT_DISPLAY,
                    color: HT.text,
                    textShadow: '0 2px 0 rgba(255,255,255,0.5)',
                  }}>{attachedCar.word_en}</span>
                )
              ) : (
                <span style={{
                  fontSize: compact ? 26 : 'clamp(24px, 3.5vw, 44px)',
                  color: HT.white,
                  textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                  fontFamily: FONT_DISPLAY, fontWeight: 800,
                }}>?</span>
              )}
            </div>
          </div>

          {/* Engine */}
          <div style={{
            position: 'relative',
            width: compact ? '34%' : 'clamp(130px, 19vw, 230px)',
            flexShrink: 0,
            animation: chugging ? 'none' : 'tcBob 1.5s ease-in-out infinite',
          }}>
            {chugging && (
              <>
                <div style={{
                  position: 'absolute', top: '-30%', right: '20%', fontSize: '1.8vw',
                  animation: 'tcSmoke 0.8s ease-out infinite',
                }}>💨</div>
                <div style={{
                  position: 'absolute', top: '-40%', right: '30%', fontSize: '1.4vw',
                  animation: 'tcSmoke 0.8s ease-out 0.3s infinite',
                }}>💨</div>
              </>
            )}
            <img
              src={`${ASSETS}/train.png`}
              alt=""
              style={{
                width: '100%', display: 'block', pointerEvents: 'none',
                transform: 'scaleX(-1)',
              }}
            />
          </div>
        </div>

        {/* Tray (bottom) — uses letter_box.png as the wood plank background.
            Kept short (max ~780px → ~28% tall) so its top edge stays below the
            train sitting on the track at bottom:34%. */}
        <div style={{
          position: 'absolute',
          bottom: '3%', left: '50%',
          transform: 'translateX(-50%)',
          width: compact ? '96%' : 'clamp(420px, 56%, 780px)',
          aspectRatio: '2172 / 724',
        }}>
          <img
            src={`${ASSETS}/letter_box.png`}
            alt=""
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'fill',
              pointerEvents: 'none',
            }}
          />
          <div style={{
            position: 'absolute',
            inset: '12% 5% 12% 5%',
            display: 'flex', gap: '2%',
            alignItems: 'center', justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            {cars.map(c => (
              <TrayCar
                key={c.id}
                car={c}
                isDragging={drag?.id === c.id}
                isHidden={attachedCar?.id === c.id || flying?.id === c.id}
                dragPoint={drag?.id === c.id ? drag : null}
                tileRef={el => carRefs.current[c.id] = el}
                onPointerStart={(e) => startDrag(e, c)}
              />
            ))}
          </div>
        </div>

        {/* Confetti when correct */}
        {grading === 'correct' && <Celebration />}

        {/* Wrong banner */}
        {grading === 'wrong' && (
          <div style={{
            position: 'absolute',
            top: '40%', left: '50%', transform: 'translateX(-50%)',
            padding: '6px 14px',
            background: HT.pinkBg,
            border: `2px solid ${HT.coral}`,
            borderRadius: 999,
            color: HT.coralDark,
            fontWeight: 800,
            fontSize: 'clamp(11px, 1.3vw, 15px)',
            fontFamily: FONT_DISPLAY,
            zIndex: 4,
            display: 'flex', gap: 10, alignItems: 'center',
            boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
            whiteSpace: 'nowrap',
          }}>
            😅 Chưa đúng! Thử toa khác nhé
          </div>
        )}

        {/* Reveal-answer button — appears after the first wrong try, stays until used */}
        {retries >= 1 && grading !== 'correct' && grading !== 'revealed' && (
          <button
            onClick={revealAnswer}
            style={{
              position: 'absolute',
              top: '48%', left: '50%', transform: 'translateX(-50%)',
              padding: '8px 18px', borderRadius: 999, border: `2px solid ${HT.white}`,
              background: HT.teal, color: HT.white,
              fontSize: 'clamp(12px, 1.3vw, 15px)', fontWeight: 800,
              fontFamily: FONT_DISPLAY, cursor: 'pointer', zIndex: 4,
              boxShadow: '0 3px 10px rgba(0,0,0,0.2)', whiteSpace: 'nowrap',
            }}
          >👀 Xem đáp án</button>
        )}

        {/* Bottom status overlays */}
        {grading === 'correct' && (
          <div style={statusOverlay(HT.greenBg, HT.greenDark)}>
            🎉 Tu... tu! Toa <b style={{ color: HT.greenDark }}>{payload.answer_word_en || attachedCar?.word_en}</b> ghép đúng câu.
          </div>
        )}
        {grading === 'revealed' && (
          <div style={statusOverlay(HT.pinkBg, HT.coral)}>
            👀 Đáp án là <b style={{ color: HT.coralDark }}>{payload.answer_word_en}</b> — lần này chưa tính điểm, lần sau bé tự chọn nhé!
          </div>
        )}
      </div>

      {/* Flying car overlay — animates from tray to slot (position: fixed) */}
      {flying && (
        <div
          style={{
            position: 'fixed',
            left: flying.startX, top: flying.startY,
            width: compact ? '22%' : 'clamp(64px, 8vw, 110px)',
            height: 'clamp(64px, 8vw, 110px)',
            background: HT.white,
            border: `3px solid ${HT.yellow}`,
            borderRadius: 12,
            boxShadow: `0 4px 12px rgba(0,0,0,0.3)`,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: 4, gap: 2,
            pointerEvents: 'none',
            zIndex: 9999,
            animation: 'tcFly 0.45s cubic-bezier(.5,.05,.4,1.1) forwards',
            '--toX': `${flying.toX}px`,
            '--toY': `${flying.toY}px`,
          }}
        >
          {flying.image_url ? (
            <img
              src={flying.image_url} alt=""
              style={{ width: '72%', height: '60%', objectFit: 'contain' }}
            />
          ) : (
            <div style={{ fontSize: '2vw' }}>📦</div>
          )}
          <div style={{
            fontSize: compact ? 12 : 'clamp(10px, 1.1vw, 14px)',
            fontWeight: 800, fontFamily: FONT_DISPLAY,
            color: HT.text,
            textAlign: 'center',
            lineHeight: 1,
          }}>
            {flying.word_en}
          </div>
        </div>
      )}
    </div>
  );
}

function statusOverlay(bg, borderColor) {
  return {
    position: 'absolute',
    left: '50%', bottom: '3%',
    transform: 'translateX(-50%)',
    maxWidth: '88%',
    padding: '10px 18px',
    background: bg,
    border: `2px solid ${borderColor}`,
    borderRadius: 14,
    textAlign: 'center',
    fontSize: 'clamp(12px, 1.4vw, 16px)',
    fontWeight: 700,
    color: HT.text,
    fontFamily: FONT_BODY,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    zIndex: 6,
    whiteSpace: 'nowrap',
  };
}

function miniBtn(bg) {
  return {
    padding: '4px 10px', borderRadius: 999, border: 'none',
    background: bg, color: HT.white,
    fontSize: 12, fontWeight: 800, cursor: 'pointer',
    fontFamily: FONT_DISPLAY,
  };
}

function TrayCar({ car, isDragging, isHidden, dragPoint, onPointerStart, tileRef }) {
  const style = {
    width: 'clamp(64px, 8vw, 110px)',
    height: 'clamp(64px, 8vw, 110px)',
    background: HT.white,
    border: `3px solid ${HT.yellow}`,
    borderRadius: 12,
    boxShadow: `0 4px 0 ${HT.yellowShadow}`,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: 4, gap: 2,
    cursor: 'grab', userSelect: 'none', touchAction: 'none',
  };
  const content = (
    <>
      {car.image_url ? (
        <img
          src={car.image_url} alt=""
          style={{ width: '72%', height: '60%', objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{ fontSize: '2vw' }}>📦</div>
      )}
      <div style={{
        fontSize: 'clamp(10px, 1.1vw, 14px)',
        fontWeight: 800, fontFamily: FONT_DISPLAY,
        color: HT.text,
        textAlign: 'center',
        lineHeight: 1,
      }}>
        {car.word_en}
      </div>
    </>
  );
  // Stable slot — visibility:hidden keeps layout from shifting while dragging/flying.
  const slotHidden = isHidden || isDragging;
  const slot = (
    <div
      ref={tileRef}
      onMouseDown={slotHidden ? undefined : onPointerStart}
      onTouchStart={slotHidden ? undefined : onPointerStart}
      style={{ ...style, visibility: slotHidden ? 'hidden' : 'visible' }}
    >
      {content}
    </div>
  );
  if (isDragging && dragPoint) {
    return (
      <>
        {slot}
        <div
          className="tc-car-dragging"
          style={{
            ...style,
            position: 'fixed',
            left: dragPoint.x - 40,
            top: dragPoint.y - 40,
          }}
        >
          {content}
        </div>
      </>
    );
  }
  return slot;
}

function Celebration() {
  const COLORS = [HT.yellow, HT.coral, HT.green, HT.teal, HT.pink];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 5 }}>
      {Array.from({ length: 28 }).map((_, i) => {
        const angle = (i / 28) * Math.PI * 2;
        const dist = 200 + Math.random() * 120;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist - 80;
        const dr = (Math.random() - 0.5) * 720;
        return (
          <div key={i} style={{
            position: 'absolute',
            left: '50%', top: '40%',
            width: 10, height: 14,
            background: COLORS[i % COLORS.length],
            borderRadius: i % 2 ? 2 : '50%',
            animation: 'tcRibbon 1.4s ease-out forwards',
            '--dx': `${dx}px`, '--dy': `${dy}px`, '--dr': `${dr}deg`,
          }} />
        );
      })}
    </div>
  );
}
